import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React, { useMemo } from 'react';
import * as Yup from 'yup';

import { Form as CustomForm } from 'src/components/common/form/Form';
import { Loading } from 'src/components/common/loading/Loading';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { useCollectionEntryNameValidation } from '../sharedValidation';

export const RenameCollectionEntryModal = ({
  collectionId,
  collectionEntryId,
  isOpen,
  onClose,
}: {
  collectionId: string;
  collectionEntryId: string;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const trackCustomEvent = useTrackCustomEvent();

  const [mutationErrorMessage, setMutationErrorMessage] =
    React.useState<string>();

  const { data, loading: dataLoading } = useQuery<
    GraphQLTypes.RenameCollectionEntryModalQuery,
    GraphQLTypes.RenameCollectionEntryModalQueryVariables
  >(
    gql`
      query RenameCollectionEntryModalQuery(
        $collectionId: ID!
        $collectionEntryId: ID!
      ) {
        operationCollection(id: $collectionId) {
          ... on OperationCollection {
            id
            operation(id: $collectionEntryId) {
              ... on OperationCollectionEntry {
                id
                name
              }
            }
          }
        }
      }
    `,
    { variables: { collectionId, collectionEntryId } },
  );

  const [renameOperationMutation] = useMutation<
    GraphQLTypes.RenameOperation,
    GraphQLTypes.RenameOperationVariables
  >(
    gql`
      mutation RenameOperation(
        $collectionId: ID!
        $collectionEntryId: ID!
        $name: String!
      ) {
        operationCollection(id: $collectionId) {
          operation(id: $collectionEntryId) {
            ... on OperationCollectionEntryMutation {
              updateName(name: $name) {
                ... on OperationCollectionEntry {
                  id
                  name
                }
                ... on Error {
                  message
                }
              }
            }
          }
        }
      }
    `,
    { onError: (error) => setMutationErrorMessage(error.message) },
  );

  const operationCollectionEntry =
    data?.operationCollection.__typename === 'OperationCollection' &&
    data?.operationCollection.operation?.__typename ===
      'OperationCollectionEntry'
      ? data.operationCollection.operation
      : undefined;

  const {
    collectionEntryNameValidation,
    loading: collectionEntryNameValidationLoading,
  } = useCollectionEntryNameValidation({
    collectionIdOrNew: collectionId,
    collectionEntryId,
  });
  /**
   * Schema for the form
   */
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        collectionEntryName: collectionEntryNameValidation,
      }),
    [collectionEntryNameValidation],
  );

  const {
    errors,
    touched,
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
  } = useFormik<Yup.InferType<typeof validationSchema>>({
    initialValues: {
      collectionEntryName: operationCollectionEntry?.name ?? '',
    },
    enableReinitialize: true,
    async onSubmit(formValues) {
      try {
        const result = await renameOperationMutation({
          variables: {
            collectionId,
            collectionEntryId,
            name: formValues.collectionEntryName,
          },
        });
        const updateOperation = result.data?.operationCollection?.operation;
        const updateName =
          updateOperation?.__typename === 'OperationCollectionEntryMutation'
            ? updateOperation.updateName
            : undefined;
        if (updateName?.__typename !== 'OperationCollectionEntry') {
          setMutationErrorMessage(
            updateName?.message ?? 'Could not rename operation',
          );
        } else {
          trackCustomEvent({
            action: 'rename_collection_entry',
            category: 'Operation Collections',
          });
          onClose();
        }
      } catch {
        // Captured via `onError`
      }
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: true,
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <CustomForm onSubmit={handleSubmit} noValidate={true}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Rename Operation</ModalTitle>
          </ModalHeader>
          <ModalBody>
            {dataLoading || collectionEntryNameValidationLoading ? (
              <Loading />
            ) : !operationCollectionEntry ? (
              <div>Could not fetch operation to rename</div>
            ) : (
              <>
                <FormControl
                  isRequired
                  isInvalid={
                    !!errors.collectionEntryName &&
                    !!touched.collectionEntryName
                  }
                >
                  <FormLabel>Operation Name</FormLabel>
                  <Input
                    autoFocus
                    isDisabled={isSubmitting}
                    name="collectionEntryName"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="YourOperationName"
                    value={values.collectionEntryName}
                  />
                  <FormErrorMessage>
                    {errors.collectionEntryName}
                  </FormErrorMessage>
                </FormControl>
                {mutationErrorMessage && (
                  <ErrorMessage>{mutationErrorMessage}</ErrorMessage>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button variant="secondary" onClick={onClose} type="button">
                Cancel
              </Button>
              <Tooltip
                disabled={Object.entries(errors).length === 0}
                label={Object.values(errors).flatMap((error, i) => (
                  <>
                    {i !== 0 && <br />}
                    {error}
                  </>
                ))}
              >
                <Button
                  variant="primary"
                  isDisabled={
                    collectionEntryNameValidationLoading ||
                    !operationCollectionEntry ||
                    Object.entries(errors).length !== 0
                  }
                  isLoading={isSubmitting}
                  loadingText="Rename"
                  type="submit"
                >
                  Rename
                </Button>
              </Tooltip>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </CustomForm>
    </Modal>
  );
};
